import type { Message, ProposedEditData } from '@/components/layout/chat-pane.types'
import { isPendingProposedEdit } from '@/lib/proposed-edit-utils'

const storageKey = (conversationId: string) => `maater-pending-proposed-edits:${conversationId}`

function asPendingEdit(value: unknown): ProposedEditData | null {
  if (!value || typeof value !== 'object') return null
  const row = value as ProposedEditData
  if (!isPendingProposedEdit(row)) return null
  if (typeof row.original !== 'string' || typeof row.proposed !== 'string') return null
  return row
}

export function pendingProposedEditsFromMetadata(metadata: unknown): ProposedEditData[] {
  if (!metadata || typeof metadata !== 'object') return []
  const raw = (metadata as { pendingProposedEdits?: unknown }).pendingProposedEdits
  if (!Array.isArray(raw)) return []
  return raw.map(asPendingEdit).filter((edit): edit is ProposedEditData => edit != null)
}

export function readPendingProposedEdits(conversationId: string): ProposedEditData[] | null {
  if (!conversationId || typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(storageKey(conversationId))
    if (raw == null) return null
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map(asPendingEdit).filter((edit): edit is ProposedEditData => edit != null)
  } catch {
    return null
  }
}

export function writePendingProposedEdits(conversationId: string, edits: ProposedEditData[]): void {
  if (!conversationId || typeof sessionStorage === 'undefined') return
  try {
    const pending = edits.filter(isPendingProposedEdit)
    sessionStorage.setItem(storageKey(conversationId), JSON.stringify(pending))
  } catch {
    // The tab copy is a convenience. A full note can exceed the quota.
  }
}

export function pendingProposedEditsInMessages(messages: Message[]): ProposedEditData[] {
  const seen = new Set<string>()
  const out: ProposedEditData[] = []
  for (const message of messages) {
    const edit = message.proposedEdit
    if (!isPendingProposedEdit(edit) || !edit) continue
    const key = edit.sessionEditId || edit.documentId
    if (seen.has(key)) continue
    seen.add(key)
    out.push(edit)
  }
  return out
}

/** Drop in-memory pending cards and insert the stored ones. An empty list means the decision was saved. */
export function replacePendingProposedEditCards(messages: Message[], pending: ProposedEditData[]): Message[] {
  const kept = messages.filter((message) => !isPendingProposedEdit(message.proposedEdit))
  if (pending.length === 0) return kept
  const cards: Message[] = pending.map((edit) => ({
    role: 'assistant',
    content: '',
    proposedEdit: edit,
    timestamp: new Date(),
  }))
  const assistantAt = kept.findIndex((message) => message.role === 'assistant' && !message.proposedEdit)
  if (assistantAt < 0) return [...kept, ...cards]
  return [...kept.slice(0, assistantAt), ...cards, ...kept.slice(assistantAt)]
}
